/**
 * Shell UI strings (sidebar groups, top bar, auth layout) for EN / TR / AR.
 * Merged into locale packs at i18n init time.
 */
export const uiLocales = {
  en: {
    admin: { soldTo: "Sold to", usedBy: "Used by" },
    invoiceOcr: {
      high: "High",
      medium: "Medium",
      low: "Low",
      confidenceOf: "{{level}} confidence: {{pct}}%",
      loadError: "Failed to load the file.",
      invoiceNoRequired: "Invoice number is required.",
      companyRequired: "Company name is required.",
      itemsRequired: "Every line item needs a description.",
      saved: "Invoice saved successfully!",
      saveError: "The invoice could not be saved. Please try again.",
      fileNotFound: "File not found.",
      goBack: "Go back",
      title: "Invoice OCR",
      reparse: "Re-parse",
      approveSave: "Approve & save",
      invoiceInfo: "Invoice details",
      invoiceNo: "Invoice no.",
      companyName: "Company name",
      companyPlaceholder: "ABC Trading Ltd.",
      taxId: "Tax ID",
      date: "Date",
      taxRatePct: "VAT rate (%)",
      taxRate: "VAT rate",
      items: "Line items",
      addItem: "Add item",
      description: "Description",
      quantity: "Quantity",
      unitPrice: "Unit price",
      amount: "Amount",
      itemPlaceholder: "Product / service name",
      removeItem: "Remove item",
      subtotal: "Subtotal:",
      vat: "VAT ({{rate}}%):",
      grandTotal: "Grand total:",
      rawText: "Raw OCR text",
      noOcrText: "No OCR text found.",
      overallScore: "Overall confidence",
      levelConfidence: "{{level}} confidence",
      fieldScores: "Field confidence",
      total: "Total amount",
      legend: "Confidence colors",
      legendHigh: "≥ 80% — High confidence",
      legendMedium: "50% – 79% — Medium confidence",
      legendLow: "< 50% — Low confidence",
      actions: "Actions",
      viewOcr: "View OCR result",
      fileDetail: "File details",
      invoiceList: "Invoice list"
    },
    ai: {
      chat: {
        disclaimer: "AI responses are based on your documents. Verify important information.",
        you: "You",
        assistant: "AI Assistant",
        answerFound: "I found {{count}} document(s) in your library related to your question. Most relevant excerpts:",
        answerNone: "I couldn't find anything about that in your documents. Try different keywords or upload the related document.",
        answerError: "The answer could not be prepared. Please try again.",
        historyError: "Chat history could not be loaded.",
        saveError: "The conversation could not be saved."
      },
      ocr: { saveFailed: "Could not save changes.", editBtn: "Edit", saveBtn: "Save" }
    },
    files: {
      noFilesInFolder: "This folder has no files yet.",
      preview: { unsupported: "This file type cannot be previewed" },
      detail: { noDownload: "No downloadable copy is stored for this file." },
      upload: { notProcessable: "OCR will be skipped (>500MB)" }
    },
    common: {
      yes: "Yes",
      no: "No",
      copied: "Link copied!",
      clear: "Clear"
    },
    navGroups: {
      general: "General",
      admin: "Administration",
      accounting: "Accounting",
      inventory: "Inventory",
      procurement: "Procurement",
      manufacturing: "Manufacturing",
      quality: "Quality & Packaging",
      export: "Export & Logistics",
      sales: "Sales",
      afterSales: "After-sales",
      enterprise: "Enterprise",
      settings: "Settings"
    },
    shell: {
      readOnly: "Your role does not have write access (viewer / read-only).",
      demoReadOnly: "Demo account: you can browse every module, but changes are disabled.",
      menuSearch: "Search menu…",
      noMenuResults: "No matching pages",
      quickUpload: "Upload file",
      openMenu: "Open menu",
      closeMenu: "Close menu",
      searchPlaceholder: "Search files, invoices, products…",
      notifications: "Notifications",
      signedInAs: "Signed in as",
      home: "Home",
      demoBadge: "Demo"
    },
    authShell: {
      tagline: "All your documents, finance and supply chain in one secure workspace.",
      feature1: "Secure cloud storage with version history",
      feature2: "AI-powered OCR for invoices and documents",
      feature3: "End-to-end inventory, procurement and sales",
      showPassword: "Show password",
      hidePassword: "Hide password",
      email: "Email",
      password: "Password"
    },
    dash: {
      overview: "Here is an overview of your workspace today.",
      scmSnapshot: "Supply chain snapshot",
      fullReports: "Full reports",
      sales: "Sales orders",
      pos: "Purchase orders",
      mfg: "Manufacturing",
      approvals: "Pending approvals",
      lowStock: "Low stock",
      openRma: "Open RMA",
      unread: "unread",
      colName: "Name",
      colSize: "Size",
      colStatus: "Status",
      colDate: "Date"
    }
  },
  tr: {
    admin: { soldTo: "Satılan firma", usedBy: "Kullanan firma" },
    invoiceOcr: {
      high: "Yüksek",
      medium: "Orta",
      low: "Düşük",
      confidenceOf: "{{level}} güven: {{pct}}%",
      loadError: "Dosya yüklenirken hata oluştu.",
      invoiceNoRequired: "Fatura numarası zorunludur.",
      companyRequired: "Firma adı zorunludur.",
      itemsRequired: "Tüm kalemlerin açıklaması olmalıdır.",
      saved: "Fatura başarıyla kaydedildi!",
      saveError: "Fatura kaydedilirken hata oluştu. Lütfen tekrar deneyin.",
      fileNotFound: "Dosya bulunamadı.",
      goBack: "Geri Dön",
      title: "Fatura OCR",
      reparse: "Yeniden Ayrıştır",
      approveSave: "Onayla ve Kaydet",
      invoiceInfo: "Fatura Bilgileri",
      invoiceNo: "Fatura No",
      companyName: "Firma Adı",
      companyPlaceholder: "ABC Ticaret Ltd. Şti.",
      taxId: "Vergi No",
      date: "Tarih",
      taxRatePct: "KDV Oranı (%)",
      taxRate: "KDV Oranı",
      items: "Kalemler",
      addItem: "Kalem Ekle",
      description: "Açıklama",
      quantity: "Miktar",
      unitPrice: "Birim Fiyat",
      amount: "Tutar",
      itemPlaceholder: "Ürün / Hizmet adı",
      removeItem: "Kalemi kaldır",
      subtotal: "Ara Toplam:",
      vat: "KDV (%{{rate}}):",
      grandTotal: "Genel Toplam:",
      rawText: "Ham OCR Metni",
      noOcrText: "OCR metni bulunamadı.",
      overallScore: "Genel Güven Skoru",
      levelConfidence: "{{level}} Güven",
      fieldScores: "Alan Güven Skorları",
      total: "Toplam Tutar",
      legend: "Güven Renkleri",
      legendHigh: "≥ 80% — Yüksek güven",
      legendMedium: "50% – 79% — Orta güven",
      legendLow: "< 50% — Düşük güven",
      actions: "İşlemler",
      viewOcr: "OCR Sonucu Görüntüle",
      fileDetail: "Dosya Detayı",
      invoiceList: "Faturalar Listesi"
    },
    ai: {
      chat: {
        disclaimer: "AI yanıtları belgelerinize dayanır. Önemli bilgileri doğrulayın.",
        you: "Siz",
        assistant: "AI Asistan",
        answerFound: "Kütüphanenizde sorunuzla ilgili {{count}} belge buldum. En ilgili bölümler:",
        answerNone: "Belgelerinizde bununla ilgili bir şey bulamadım. Farklı anahtar kelimeler deneyin veya ilgili belgeyi yükleyin.",
        answerError: "Yanıt hazırlanamadı. Lütfen tekrar deneyin.",
        historyError: "Sohbet geçmişi yüklenemedi.",
        saveError: "Sohbet kaydedilemedi."
      },
      ocr: { saveFailed: "Değişiklikler kaydedilemedi.", editBtn: "Düzenle", saveBtn: "Kaydet" }
    },
    files: {
      noFilesInFolder: "Bu klasörde henüz dosya yok.",
      preview: { unsupported: "Bu dosya türü önizlenemiyor" },
      detail: { noDownload: "Bu dosya için indirilebilir bir kopya bulunmuyor." },
      upload: { notProcessable: "OCR atlanacak (>500MB)" }
    },
    common: {
      yes: "Evet",
      no: "Hayır",
      copied: "Bağlantı kopyalandı!",
      clear: "Temizle"
    },
    navGroups: {
      general: "Genel",
      admin: "Yönetim",
      accounting: "Muhasebe",
      inventory: "Stok",
      procurement: "Satın Alma",
      manufacturing: "Üretim",
      quality: "Kalite ve Paketleme",
      export: "İhracat ve Lojistik",
      sales: "Satış",
      afterSales: "Satış Sonrası",
      enterprise: "Kurumsal",
      settings: "Ayarlar"
    },
    shell: {
      readOnly: "Bu rol ile yazma yetkiniz bulunmuyor (Viewer veya salt okunur).",
      demoReadOnly: "Demo hesabı: tüm modülleri inceleyebilirsiniz, değişiklik yapılamaz.",
      menuSearch: "Menüde ara…",
      noMenuResults: "Eşleşen sayfa yok",
      quickUpload: "Dosya yükle",
      openMenu: "Menüyü aç",
      closeMenu: "Menüyü kapat",
      searchPlaceholder: "Dosya, fatura, ürün ara…",
      notifications: "Bildirimler",
      signedInAs: "Giriş yapan",
      home: "Ana Sayfa",
      demoBadge: "Demo"
    },
    authShell: {
      tagline: "Tüm belgeleriniz, finansınız ve tedarik zinciriniz tek bir güvenli çalışma alanında.",
      feature1: "Sürüm geçmişli güvenli bulut depolama",
      feature2: "Fatura ve belgeler için yapay zekâ destekli OCR",
      feature3: "Uçtan uca stok, satın alma ve satış yönetimi",
      showPassword: "Şifreyi göster",
      hidePassword: "Şifreyi gizle",
      email: "E-posta",
      password: "Şifre"
    },
    dash: {
      overview: "Çalışma alanınızın bugünkü özeti.",
      scmSnapshot: "Tedarik zinciri özeti",
      fullReports: "Tüm raporlar",
      sales: "Satış siparişleri",
      pos: "Satın alma siparişleri",
      mfg: "Üretim",
      approvals: "Bekleyen onaylar",
      lowStock: "Düşük stok",
      openRma: "Açık RMA",
      unread: "okunmamış",
      colName: "Ad",
      colSize: "Boyut",
      colStatus: "Durum",
      colDate: "Tarih"
    }
  },
  ar: {
    admin: { soldTo: "بيع إلى", usedBy: "مستخدم من قبل" },
    invoiceOcr: {
      high: "مرتفعة",
      medium: "متوسطة",
      low: "منخفضة",
      confidenceOf: "ثقة {{level}}: {{pct}}%",
      loadError: "تعذر تحميل الملف.",
      invoiceNoRequired: "رقم الفاتورة مطلوب.",
      companyRequired: "اسم الشركة مطلوب.",
      itemsRequired: "يجب أن يحتوي كل بند على وصف.",
      saved: "تم حفظ الفاتورة بنجاح!",
      saveError: "تعذر حفظ الفاتورة. يرجى المحاولة مرة أخرى.",
      fileNotFound: "الملف غير موجود.",
      goBack: "رجوع",
      title: "OCR الفاتورة",
      reparse: "إعادة التحليل",
      approveSave: "اعتماد وحفظ",
      invoiceInfo: "تفاصيل الفاتورة",
      invoiceNo: "رقم الفاتورة",
      companyName: "اسم الشركة",
      companyPlaceholder: "شركة ABC التجارية",
      taxId: "الرقم الضريبي",
      date: "التاريخ",
      taxRatePct: "نسبة الضريبة (%)",
      taxRate: "نسبة الضريبة",
      items: "البنود",
      addItem: "إضافة بند",
      description: "الوصف",
      quantity: "الكمية",
      unitPrice: "سعر الوحدة",
      amount: "المبلغ",
      itemPlaceholder: "اسم المنتج / الخدمة",
      removeItem: "إزالة البند",
      subtotal: "المجموع الفرعي:",
      vat: "الضريبة ({{rate}}%):",
      grandTotal: "الإجمالي:",
      rawText: "نص OCR الخام",
      noOcrText: "لم يتم العثور على نص OCR.",
      overallScore: "الثقة الإجمالية",
      levelConfidence: "ثقة {{level}}",
      fieldScores: "ثقة الحقول",
      total: "المبلغ الإجمالي",
      legend: "ألوان الثقة",
      legendHigh: "≥ 80% — ثقة مرتفعة",
      legendMedium: "50% – 79% — ثقة متوسطة",
      legendLow: "< 50% — ثقة منخفضة",
      actions: "الإجراءات",
      viewOcr: "عرض نتيجة OCR",
      fileDetail: "تفاصيل الملف",
      invoiceList: "قائمة الفواتير"
    },
    ai: {
      chat: {
        disclaimer: "تعتمد ردود الذكاء الاصطناعي على مستنداتك. تحقق من المعلومات المهمة.",
        you: "أنت",
        assistant: "مساعد الذكاء الاصطناعي",
        answerFound: "وجدت {{count}} مستند(ات) في مكتبتك مرتبطة بسؤالك. أهم المقتطفات:",
        answerNone: "لم أجد أي شيء عن ذلك في مستنداتك. جرّب كلمات مفتاحية مختلفة أو ارفع المستند ذي الصلة.",
        answerError: "تعذر إعداد الإجابة. يرجى المحاولة مرة أخرى.",
        historyError: "تعذر تحميل سجل المحادثة.",
        saveError: "تعذر حفظ المحادثة."
      },
      ocr: { saveFailed: "تعذر حفظ التغييرات.", editBtn: "تعديل", saveBtn: "حفظ" }
    },
    files: {
      noFilesInFolder: "لا توجد ملفات في هذا المجلد بعد.",
      preview: { unsupported: "لا يمكن معاينة هذا النوع من الملفات" },
      detail: { noDownload: "لا توجد نسخة قابلة للتنزيل لهذا الملف." },
      upload: { notProcessable: "سيتم تخطي OCR (أكبر من 500 ميغابايت)" }
    },
    common: {
      yes: "نعم",
      no: "لا",
      copied: "تم نسخ الرابط!",
      clear: "مسح"
    },
    navGroups: {
      general: "عام",
      admin: "الإدارة",
      accounting: "المحاسبة",
      inventory: "المخزون",
      procurement: "المشتريات",
      manufacturing: "التصنيع",
      quality: "الجودة والتغليف",
      export: "التصدير والخدمات اللوجستية",
      sales: "المبيعات",
      afterSales: "ما بعد البيع",
      enterprise: "المؤسسة",
      settings: "الإعدادات"
    },
    shell: {
      readOnly: "لا يملك دورك صلاحية الكتابة (عارض / للقراءة فقط).",
      demoReadOnly: "حساب تجريبي: يمكنك تصفح جميع الوحدات، لكن التعديلات معطّلة.",
      menuSearch: "ابحث في القائمة…",
      noMenuResults: "لا توجد صفحات مطابقة",
      quickUpload: "رفع ملف",
      openMenu: "فتح القائمة",
      closeMenu: "إغلاق القائمة",
      searchPlaceholder: "ابحث عن الملفات والفواتير والمنتجات…",
      notifications: "الإشعارات",
      signedInAs: "تم تسجيل الدخول باسم",
      home: "الرئيسية",
      demoBadge: "تجريبي"
    },
    authShell: {
      tagline: "جميع مستنداتك وماليتك وسلسلة التوريد في مساحة عمل آمنة واحدة.",
      feature1: "تخزين سحابي آمن مع سجل الإصدارات",
      feature2: "تعرف ضوئي على الفواتير والمستندات مدعوم بالذكاء الاصطناعي",
      feature3: "إدارة متكاملة للمخزون والمشتريات والمبيعات",
      showPassword: "إظهار كلمة المرور",
      hidePassword: "إخفاء كلمة المرور",
      email: "البريد الإلكتروني",
      password: "كلمة المرور"
    },
    dash: {
      overview: "إليك نظرة عامة على مساحة عملك اليوم.",
      scmSnapshot: "ملخص سلسلة التوريد",
      fullReports: "جميع التقارير",
      sales: "أوامر البيع",
      pos: "أوامر الشراء",
      mfg: "التصنيع",
      approvals: "موافقات معلقة",
      lowStock: "مخزون منخفض",
      openRma: "RMA مفتوحة",
      unread: "غير مقروءة",
      colName: "الاسم",
      colSize: "الحجم",
      colStatus: "الحالة",
      colDate: "التاريخ"
    }
  }
};
